import React, { useContext } from 'react';
import { decode } from 'html-entities';
import { useInView } from 'react-intersection-observer';

// PARTIALS & HELPERS.
const Button = React.lazy(() => import('src/partials/button'));
const Clicker = React.lazy(() => import('src/helpers/clicker'));
const Imaging = React.lazy(() => import('src/helpers/imaging'));
import { Overlay, TitleBlurbContainer } from './styles';
import HtmlParser from 'src/helpers/html-parser';
import { AppContext } from 'src/contexts/app';

const Start = ({ data, settings, children, ...otherProps }) => {
    let bgTone;
    let styles = {};
    if (data.carousel_items && data.carousel_items.length && data.carousel_items[0].background_value == "dark") {
        bgTone = "bg-dark";
        styles.backgroundColor = settings.design?.colors?.primary_color;
    } else {
        bgTone = "";
        styles.backgroundColor = "#FFF";
    }

    const { ref, inView } = useInView({
        triggerOnce: true,
        fallbackInView: true
    });
    return (<div ref={ref} class={`hero-unconstrained ${data.mvk_mod_layout} ${data.style} ${bgTone}`} style={styles} children={inView ? children : null} {...otherProps} />);
};
Start.displayName = 'modules, hero-unconstrained, layout, Start';

Start.Container = ({ data, settings, children, ...otherProps }) => {
    return (<div class={`hero-container ${data.mvk_mod_layout} ${data.style}`} children={children} {...otherProps} />);
};
Start.Container.displayName = 'modules, hero-unconstrained, layout, Start.Container';

Start.Container.TextBelowCarousel = ({ data, settings, placeholders, children, ...otherProps }) => {
    const title = (placeholders && data.title_selection === 'dynamic') ? placeholders.single_line[data?.title] : data?.title

    if (data.style == 'text-on-carousel' || (!title && !data.blurb)) return null;
    return (
        <div class='title-blurb-container grid-container'>
            {title && <h1>{decode(title)}</h1>}
            {data.blurb && <p><HtmlParser html={data.blurb} placeholders={placeholders} /></p>}
        </div>
    );
};
Start.Container.TextBelowCarousel.displayName = 'modules, hero-unconstrained, layout, Start.Container.TextBelowCarousel';

Start.Container.Carousel = ({ data, settings, carouselClass, children, ...otherProps }) => {

    if (data.carousel_items) {
        return (
            <div class={`slider-container ${data.type} ${data.style}${carouselClass}`}>
                {children}
            </div>
        );
    } else {
        return null;
    }
};
Start.Container.Carousel.displayName = 'modules, hero-unconstrained, layout, Start.Container.Carousel';

Start.Container.Carousel.Slide = ({ data, placeholders, slide, key }) => {
    const title = (placeholders && slide.slide_title_selection === 'dynamic') ? placeholders.single_line[slide.carousel_title] : slide.carousel_title
    const link = placeholders ? placeholders.button_link[slide.carousel_image_link] : slide.carousel_image_link
    const image = (placeholders && slide.image_selection === 'dynamic') ? placeholders.image[slide.carousel_image_dynamic] : slide.carousel_image
    const imageTablet = (placeholders && slide.image_selection_tablet === 'dynamic') ? placeholders.image[slide.carousel_image_tablet_dynamic] : slide.carousel_image_tablet
    const imageMobile = (placeholders && slide.image_selection_mobile === 'dynamic') ? placeholders.image[slide.carousel_image_mobile_dynamic] : slide.carousel_image_mobile
    const appContext = useContext(AppContext);

    const titleBlurbStyles = {
        backgroundColor: slide?.title_background_color ? `${pylot?.design?.hexToRGB(slide?.title_background_color, slide?.title_background_opacity)}` : 'transparent'
    };

    if (data.style !== 'text-on-carousel') {
        return (
            <Clicker key={key} class={`slide-wrapper ${imageMobile ? 'with-mobile-image' : ''}`} type={link?.url ? 'anchor' : null} url={link?.url} target={link?.target} title={link?.title}>
                {(appContext.width >= 1200 || (!imageTablet && appContext.width >= 768) || (!imageMobile && !imageTablet)) && <Imaging className='desktop-image' data={image} />}
                {(imageTablet && appContext.width >= 768 && appContext.width < 1200) && <Imaging className='tablet-image' data={imageTablet} />}
                {(imageMobile && appContext.width < 768) && <MobileImage data={imageMobile} />}
            </Clicker>
        );
    } else {
        return (
            <Clicker key={key} type={(link && !link?.title) ? 'anchor' : null} url={(link && !link?.title) ? link?.url : null} target={link?.target ? link?.target : null} class={`slide-wrapper ${imageMobile ? 'with-mobile-image' : ''}`}>
                <Overlay className={slide.image_overlay_style} overlayColor={slide.image_overlay} overlayOpacity={slide.image_overlay_opacity} />
                {(appContext.width >= 1200 || (!imageTablet && appContext.width >= 768) || (!imageMobile && !imageTablet)) && <Imaging className='desktop-image' data={image} />}
                {(imageTablet && appContext.width >= 768 && appContext.width < 1200) && <Imaging className='tablet-image' data={imageTablet} />}
                {(imageMobile && appContext.width < 768) && <MobileImage data={imageMobile} />}
                {(title || slide.carousel_blurb || link?.title) &&
                    <TitleBlurbContainer
                        className={`title-blurb-container${slide.enable_positioning_options ? ' custom-positioning' : ''}`}
                        style={titleBlurbStyles}
                        contentPositioning={slide.enable_positioning_options ? slide.content_positioning : null}
                    >
                        {title && <h1 class={`${slide?.background_value === 'dark' ? 'white-txt' : 'primary-txt'}`}>{decode(title)}</h1>}
                        {slide.carousel_blurb && <div class={`blurb ${slide?.background_value === 'dark' ? 'white-txt' : 'body-copy-txt'}`}><HtmlParser html={slide.carousel_blurb} /></div>}
                        {link?.title &&
                            <div className={`button-wrapper ${slide.button_alignment}`}>
                                <Button title={link?.title} url={link?.url} target={link?.target} tone={slide.background_value} type={'primary'} />
                            </div>
                        }
                    </TitleBlurbContainer>
                }
            </Clicker>
        );
    }
};
Start.Container.Carousel.Slide.displayName = 'modules, hero-unconstrained, layout, Start.Container.Carousel.Slide';

export default Start;



const MobileImage = ({ data }) => {
    Object.keys(data.sizes).forEach(function (itm) {
        if (itm != "medium_large-width" && itm != "medium_large-height") delete data.sizes[itm];
    });
    return (<Imaging className='mobile-image' data={data} />);
};